/**
 * User Dashboard Component
 * 
 * Protected dashboard page for authenticated users. Provides navigation
 * to key features, displays user information, and shows recent games.
 * 
 * Features:
 * - User welcome message and account information
 * - Quick access to game creation tools
 * - Feature overview cards explaining TabPicker capabilities
 * - Recent games list (currently placeholder)
 * - Secure logout functionality
 * - Responsive layout with Tailwind CSS
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Logo from '../components/Logo';
import MobileMenu from '../components/MobileMenu';

/**
 * User Dashboard Component
 * 
 * Main dashboard page for authenticated users displaying navigation options,
 * feature highlights, and recent games overview.
 * 
 * @returns JSX element containing the user dashboard interface
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Feature flags
  const isDiscoverGamesEnabled = import.meta.env.VITE_FEATURE_DISCOVER_GAMES === 'true';

  /**
   * Navigate to game creation page
   */
  const handleCreateGame = () => {
    navigate('/create-game');
  };

  /**
   * Navigate to game comparison page
   */
  const handleCompareGames = () => {
    navigate('/compare');
  };

  /**
   * Navigate to discover games page
   */
  const handleDiscoverGames = () => {
    navigate('/discover');
  };

  /**
   * Handle user logout and clear authentication state
   */
  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-emerald-50 to-amber-50">
      <header className="bg-white shadow-sm border-b border-tp-brand/20">
        <div className="container mx-auto px-2 tablet:px-4 py-4">
          <div className="flex justify-between items-center">
            <Logo className="w-full max-w-[200px] h-auto" />

            {/* Desktop Menu */}
            <div className="hidden tablet:flex items-center gap-4">
              <span className="text-tp-line">Welcome, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="text-tp-line hover:text-tp-brand text-base tablet:text-sm transition-colors"
              >
                Sign Out
              </button>
            </div>

            {/* Mobile Menu */}
            <div className="tablet:hidden">
              <MobileMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-2 tablet:px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 mb-8 border-2 border-tp-brand/20">
            <h2 className="text-2xl tablet:text-xl font-semibold text-tp-brand mb-4">
              Get Started
            </h2>
            <p className="text-tp-line mb-6">
              Create your first pull tab game analysis to see detailed odds, payout structures, and recommendations.
            </p>
            <div className="flex flex-col tablet:flex-row gap-3">
              <button
                onClick={handleCreateGame}
                className="bg-gradient-to-r from-tp-brand to-tp-leaf hover:shadow-xl text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105"
              >
                Create Game
              </button>
              <button
                onClick={handleCompareGames}
                className="bg-gradient-to-r from-tp-gold to-tp-cherry hover:shadow-xl text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105"
              >
                Compare Deals
              </button>
              {isDiscoverGamesEnabled && (
                <button
                  onClick={handleDiscoverGames}
                  className="bg-gradient-to-r from-tp-leaf to-tp-brand hover:shadow-xl text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105"
                >
                  Discover Games
                </button>
              )}
            </div>
          </div>

          <div className={`grid gap-6 ${isDiscoverGamesEnabled ? 'tablet:grid-cols-2 desktop:grid-cols-4' : 'tablet:grid-cols-3'}`}>
            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-tp-brand hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-xl tablet:text-lg font-semibold mb-2 text-tp-brand">Quick Analysis</h3>
              <p className="text-tp-line text-base tablet:text-sm">
                Get instant odds calculations and hold percentage analysis for any pull tab game.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-tp-leaf hover:shadow-xl transition-shadow">
              <div className="text-3xl mb-3">💾</div>
              <h3 className="text-xl tablet:text-lg font-semibold mb-2 text-tp-leaf">Save Games</h3>
              <p className="text-tp-line text-base tablet:text-sm">
                Store your game analyses in the cloud for easy access and comparison.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-tp-gold cursor-pointer hover:shadow-xl transition-shadow" onClick={handleCompareGames}>
              <div className="text-3xl mb-3">📈</div>
              <h3 className="text-xl tablet:text-lg font-semibold mb-2 text-tp-gold">Compare Options</h3>
              <p className="text-tp-line text-base tablet:text-sm">
                Side-by-side comparison of multiple games with detailed metrics and rankings.
              </p>
            </div>

            {isDiscoverGamesEnabled && (
              <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-tp-cherry cursor-pointer hover:shadow-xl transition-shadow" onClick={handleDiscoverGames}>
                <div className="text-3xl mb-3">🌍</div>
                <h3 className="text-xl tablet:text-lg font-semibold mb-2 text-tp-cherry">Discover Templates</h3>
                <p className="text-tp-line text-base tablet:text-sm">
                  Find game templates shared by other TabPicker users near your location.
                </p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-8 mt-8 border-2 border-tp-brand/20">
            <h2 className="text-2xl tablet:text-xl font-semibold text-tp-brand mb-4">
              Recent Games
            </h2>
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🎲</div>
              <p className="text-tp-line">
                No games created yet. Click "Create Game" to get started!
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}